import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "../../ui/dialog";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import {
    Search,
    Shield,
    Key,
    Users,
    Lock,
    Plus,
    MoreVertical,
    Copy,
    Eye,
    EyeOff,
    RefreshCw,
    Trash2,
    Edit,
    CheckCircle2,
    XCircle,
    Clock,
    AlertTriangle,
    Activity,
    Server,
    FileText,
    History,
    Ban,
    Layers,
    KeyRound,
    Building2,
    AppWindow,
    ScrollText,
    ChevronRight,
    ArrowUpDown,
    Filter,
    Download,
    RotateCcw,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { useToast } from "../../../hooks/use-toast";
import {
    createVaultApp,
    createVaultClient,
    createVaultSecret,
    createVaultToken,
    listVaultApps,
    listVaultClients,
    listVaultSecrets,
    listVaultTokens,
    deleteVaultClient,
    deleteVaultApp,
    deleteVaultSecret,
    regenerateVaultAppSecret,
    updateVaultAppScopes,
    disableVaultApp,
    enableVaultApp,
    disableVaultClient,
    enableVaultClient,
    updateVaultSecret,
    listVaultAuditLogs,
    getVaultOverview,
    VaultClientResponse,
    revokeVaultTokenById,
    revokeAppAllTokens,
    getVaultSecretValue,
    updateVaultClient,
} from "../../../services/vaultService";

// Types based on DB Schema
interface VaultClient {
    id: string;
    name: string;
    description: string;
    createdAt: string;
    updatedAt: string;
    active: boolean;
    appsCount: number;
    secretsCount: number;
}

interface VaultClientApp {
    id: string;
    clientId: string;
    clientName: string;
    name: string;
    clientSecret: string;
    scopes: ("read" | "write" | "delete")[];
    allowedLabels: string[];
    active: boolean;
    createdAt: string;
    updatedAt: string;
    activeTokens: number;
    appId: string;
}

interface VaultToken {
    id: string;
    appId: string;
    appName: string;
    clientName: string;
    token: string;
    expiresAt: string;
    revoked: boolean;
    createdAt: string;
    lastUsedAt: string | null;
    sourceIp: string;
    isExpired?: boolean;
    status?: string;
}

interface VaultSecret {
    id: string;
    clientId: string;
    clientName: string;
    label: string;
    vaultPath: string;
    version: number;
    createdAt: string;
    updatedAt: string;
    accessCount: number;
    appSecret?: string;
}

interface VaultAuditLog {
    id: string;
    timestamp: string;
    eventType: string;
    actorId: string;
    actorType: "admin" | "app";
    clientId: string;
    clientName: string;
    appId: string | null;
    appName: string | null;
    secretLabel: string | null;
    vaultPath: string | null;
    success: boolean;
    sourceIp: string;
    additionalData: string;
    requestId?: string;
    userAgent?: string;
    metadata?: string;
    principalName?: string;
}


export default function VaultServiceManagement() {
    const [activeTab, setActiveTab] = useState("overview");
    const [searchTerm, setSearchTerm] = useState("");
    const { toast } = useToast();

    const [clients, setClients] = useState<VaultClient[]>([]);
    const [clientApps, setClientApps] = useState<VaultClientApp[]>([]);
    const [tokens, setTokens] = useState<VaultToken[]>([]);
    const [secrets, setSecrets] = useState<VaultSecret[]>([]);
    const [auditLogs, setAuditLogs] = useState<VaultAuditLog[]>([]);

    const [isLoadingClients, setIsLoadingClients] = useState(false);
    const [isLoadingApps, setIsLoadingApps] = useState(false);
    const [isLoadingSecrets, setIsLoadingSecrets] = useState(false);
    const [isLoadingTokens, setIsLoadingTokens] = useState(false);
    const [isLoadingAuditLogs, setIsLoadingAuditLogs] = useState(false);
    const [isSavingClient, setIsSavingClient] = useState(false);
    const [isSavingApp, setIsSavingApp] = useState(false);
    const [isSavingSecret, setIsSavingSecret] = useState(false);
    const [issuingTokenFor, setIssuingTokenFor] = useState<string | null>(null);

    const mapClientFromApi = (client: VaultClientResponse): VaultClient => ({
        id: client.client_id || client.client_name,
        name: client.client_name,
        description: client.description || "",
        createdAt: client.created_at,
        updatedAt: client.updated_at || client.created_at,
        active: client.enabled,
        appsCount: client.apps_count || 0,
        secretsCount: client.secrets_count || 0,
    });

    const apiToUiScopes = (scopes: string[]) =>
        scopes
            .map((scope) => scope.replace("secret:", "") as "read" | "write" | "delete")
            .filter((scope) => scope === "read" || scope === "write" || scope === "delete");

    const uiToApiScopes = (scopes: string[]) =>
        scopes.map((scope) => (scope.startsWith("secret:") ? scope : `secret:${scope}`));

    const generateLocalId = () =>
        (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `local-${Date.now()}`);

    useEffect(() => {
        const fetchClients = async () => {
            setIsLoadingClients(true);
            try {
                const response = await listVaultClients();
                if (response?.clients) {
                    setClients(response.clients.map(mapClientFromApi));
                } else {
                    setClients([]);
                }
            } catch (error: any) {
                console.error("Failed to load vault clients", error);
                setClients([]);
                toast({
                    title: "Unable to load clients",
                    description: typeof error === "string" ? error : "Please try again.",
                    variant: "destructive",
                });
            } finally {
                setIsLoadingClients(false);
            }
        };

        const fetchApps = async () => {
            try {
                const response = await listVaultApps();
                if (response?.applications) {
                    const mappedApps = response.applications.map((app) => {
                        const matchingClient = clients.find(c => c.name === app.client_name);
                        return {
                            id: generateLocalId(),
                            clientId: matchingClient?.id || app.client_name,
                            clientName: app.client_name,
                            name: app.app_name,
                            clientSecret: app.app_secret ? app.app_secret : "••••••••••••••••",
                            scopes: apiToUiScopes(app.scopes || []),
                            allowedLabels: app.allowed_labels || [],
                            active: app.enabled,
                            createdAt: app.created_at,
                            updatedAt: app.updated_at || app.created_at,
                            activeTokens: (app as any).active_tokens || 0,
                            appId: (app as any).app_id || app.app_name,
                        };
                    });
                    setClientApps(mappedApps);
                } else {
                    setClientApps([]);
                }
            } catch (error: any) {
                console.error("Failed to load vault apps on mount", error);
                setClientApps([]);
            }
        };

        const fetchSecrets = async () => {
            try {
                const response = await listVaultSecrets();
                if (response?.secrets) {
                    const mappedSecrets = response.secrets.map((secret: any) => {
                        const matchingClient = clients.find(c => c.name === secret.clientName);
                        return {
                            id: generateLocalId(),
                            clientId: matchingClient?.id || secret.clientName,
                            clientName: secret.clientName,
                            label: secret.label,
                            vaultPath: secret.vaultPath,
                            version: secret.version,
                            createdAt: secret.lastUpdated,
                            updatedAt: secret.lastUpdated,
                            accessCount: secret.accessCount,
                            appSecret: secret.app_secret ? secret.app_secret : "••••••••••••••••",
                        };
                    });
                    setSecrets(mappedSecrets);
                } else {
                    setSecrets([]);
                }
            } catch (error: any) {
                console.error("Failed to load vault secrets on mount", error);
                setSecrets([]);
            }
        };

        const fetchAuditLogs = async () => {
            setIsLoadingAuditLogs(true);
            try {
                const response = await listVaultAuditLogs({ page: 0, size: 50 });
                if (response?.items) {
                    const mappedLogs = response.items.map((log: any, idx: number) => ({
                        id: log.id?.toString() || `audit-${idx}`,
                        timestamp: log.timestamp,
                        eventType: log.action?.toLowerCase().replace(/([A-Z])/g, "_$1") || "unknown",
                        actorId: "system",
                        actorType: "admin" as const,
                        clientId: log.clientId || log.clientName,
                        clientName: log.clientName,
                        appId: log.appName ? log.appName : null,
                        appName: log.appName || null,
                        secretLabel: log.resourcePath || null,
                        vaultPath: log.resourcePath || null,
                        success: log.success,
                        sourceIp: log.ipAddress || "—",
                        additionalData: log.errorMessage || "{}",
                        requestId: log.requestId,
                        userAgent: log.userAgent,
                        metadata: log.metadata,
                        principalName: log.principalName
                    }));
                    setAuditLogs(mappedLogs);
                } else {
                    setAuditLogs([]);
                }
            } catch (error: any) {
                console.error("Failed to load audit logs on mount", error);
                setAuditLogs([]);
            } finally {
                setIsLoadingAuditLogs(false);
            }
        };

        fetchClients();
        fetchApps();
        fetchSecrets();
        fetchAuditLogs();
    }, [toast]);

    useEffect(() => {
        const loadTabData = async () => {
            if (activeTab === "clients") {
                setIsLoadingClients(true);
                try {
                    const response = await listVaultClients();
                    if (response?.clients) {
                        setClients(response.clients.map(mapClientFromApi));
                    } else {
                        setClients([]);
                    }
                } catch (error: any) {
                    console.error("Failed to load vault clients", error);
                    setClients([]);
                    toast({
                        title: "Unable to load clients",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingClients(false);
                }
            } else if (activeTab === "apps") {
                setIsLoadingApps(true);
                try {
                    const response = await listVaultApps();
                    console.log("Apps API Response:", response);
                    if (response?.applications) {
                        const mappedApps = response.applications.map((app) => {
                            const matchingClient = clients.find(c => c.name === app.client_name);
                            return {
                                id: generateLocalId(),
                                clientId: matchingClient?.id || app.client_name,
                                clientName: app.client_name,
                                name: app.app_name,
                                clientSecret: app.app_secret ? app.app_secret : "••••••••••••••••",
                                scopes: apiToUiScopes(app.scopes || []),
                                allowedLabels: app.allowed_labels || [],
                                active: app.enabled,
                                createdAt: app.created_at,
                                updatedAt: app.updated_at || app.created_at,
                                activeTokens: (app as any).active_tokens || 0,
                                appId: (app as any).app_id || app.app_name,
                            };
                        });
                        setClientApps(mappedApps);
                    } else {
                        setClientApps([]);
                    }
                } catch (error: any) {
                    console.error("Failed to load vault apps", error);
                    setClientApps([]);
                    toast({
                        title: "Unable to load apps",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingApps(false);
                }
            } else if (activeTab === "secrets") {
                setIsLoadingSecrets(true);
                try {
                    const response = await listVaultSecrets();
                    console.log("Secrets API Response:", response);
                    if (response?.secrets) {
                        const mappedSecrets = response.secrets.map((secret: any) => {
                            const matchingClient = clients.find(c => c.name === secret.clientName);
                            return {
                                id: generateLocalId(),
                                clientId: matchingClient?.id || secret.clientName,
                                clientName: secret.clientName,
                                label: secret.label,
                                vaultPath: secret.vaultPath,
                                version: secret.version,
                                createdAt: secret.lastUpdated,
                                updatedAt: secret.lastUpdated,
                                accessCount: secret.accessCount,
                                appSecret: secret.app_secret ? secret.app_secret : "••••••••••••••••",
                            };
                        });
                        setSecrets(mappedSecrets);
                    } else {
                        setSecrets([]);
                    }
                } catch (error: any) {
                    console.error("Failed to load vault secrets", error);
                    setSecrets([]);
                    toast({
                        title: "Unable to load secrets",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingSecrets(false);
                }
            } else if (activeTab === "tokens") {
                setIsLoadingTokens(true);
                try {
                    const response = await listVaultTokens({ page: 0, size: 100 });
                    console.log("Tokens API Response:", response);
                    if (response?.tokens && response.tokens.length > 0) {
                        const mappedTokens = response.tokens.map((token: any) => ({
                            id: token.id?.toString() || `token-${Date.now()}`,
                            appId: token.app,
                            appName: token.app,
                            clientName: token.client,
                            token: token.token,
                            expiresAt: token.expiresAt,
                            revoked: token.isRevoked,
                            createdAt: token.created,
                            lastUsedAt: token.lastUsed === "Never" ? null : token.lastUsed,
                            sourceIp: token.sourceIp || "Not used",
                            isExpired: token.isExpired,
                            status: token.status,
                        }));
                        setTokens(mappedTokens);
                    } else {
                        setTokens([]);
                    }
                } catch (error: any) {
                    console.error("Failed to load vault tokens", error);
                    setTokens([]);
                    toast({
                        title: "Unable to load tokens",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingTokens(false);
                }
            } else if (activeTab === "audit") {
                setIsLoadingAuditLogs(true);
                try {
                    const response = await listVaultAuditLogs({ page: 0, size: 50 });
                    if (response?.items) {
                        const mappedLogs = response.items.map((log: any, idx: number) => ({
                            id: log.id?.toString() || `audit-${idx}`,
                            timestamp: log.timestamp,
                            eventType: log.action?.toLowerCase().replace(/([A-Z])/g, "_$1") || "unknown",
                            actorId: "system",
                            actorType: "admin" as const,
                            clientId: log.clientId || log.clientName,
                            clientName: log.clientName,
                            appId: log.appName ? log.appName : null,
                            appName: log.appName || null,
                            secretLabel: log.resourcePath || null,
                            vaultPath: log.resourcePath || null,
                            success: log.success,
                            sourceIp: log.ipAddress || "—",
                            additionalData: log.errorMessage || "{}",
                            requestId: log.requestId,
                            userAgent: log.userAgent,
                            metadata: log.metadata,
                            principalName: log.principalName
                        }));
                        setAuditLogs(mappedLogs);
                    } else {
                        setAuditLogs([]);
                    }
                } catch (error: any) {
                    console.error("Failed to load audit logs", error);
                } finally {
                    setIsLoadingAuditLogs(false);
                }
            } else if (activeTab === "overview") {
                try {
                    const response = await getVaultOverview();
                    console.log("Overview API Response:", response);
                } catch (error: any) {
                    console.error("Failed to load overview data", error);
                }
            }
        };

        loadTabData();
    }, [activeTab]);

    const [isCreateClientOpen, setIsCreateClientOpen] = useState(false);
    const [isCreateAppOpen, setIsCreateAppOpen] = useState(false);
    const [isCreateSecretOpen, setIsCreateSecretOpen] = useState(false);
    const [isCredentialsDialogOpen, setIsCredentialsDialogOpen] = useState(false);
    const [generatedCredentials, setGeneratedCredentials] = useState<{ clientId: string; clientSecret: string; clientName: string; appId?: string } | null>(null);
    const [selectedClient, setSelectedClient] = useState<VaultClient | null>(null);
    const [selectedApp, setSelectedApp] = useState<VaultClientApp | null>(null);
    const [showSecret, setShowSecret] = useState<string | null>(null);
    const [showGeneratedSecret, setShowGeneratedSecret] = useState(false);
    const [isViewCredentialsOpen, setIsViewCredentialsOpen] = useState(false);
    const [viewingApp, setViewingApp] = useState<VaultClientApp | null>(null);
    const [showViewingSecret, setShowViewingSecret] = useState(false);

    const [isEditClientOpen, setIsEditClientOpen] = useState(false);
    const [editingClient, setEditingClient] = useState<VaultClient | null>(null);
    const [editClientName, setEditClientName] = useState("");
    const [editClientDescription, setEditClientDescription] = useState("");
    const [isDeleteClientOpen, setIsDeleteClientOpen] = useState(false);
    const [deletingClient, setDeletingClient] = useState<VaultClient | null>(null);

    const [isEditAppOpen, setIsEditAppOpen] = useState(false);
    const [editingApp, setEditingApp] = useState<VaultClientApp | null>(null);
    const [editAppName, setEditAppName] = useState("");
    const [editAppLabels, setEditAppLabels] = useState("");
    const [editAppScopes, setEditAppScopes] = useState<string[]>([]);
    const [isDeleteAppOpen, setIsDeleteAppOpen] = useState(false);
    const [deletingApp, setDeletingApp] = useState<VaultClientApp | null>(null);
    const [isRegenerateSecretOpen, setIsRegenerateSecretOpen] = useState(false);
    const [regeneratingApp, setRegeneratingApp] = useState<VaultClientApp | null>(null);
    const [newlyGeneratedSecret, setNewlyGeneratedSecret] = useState("");
    const [showNewlyGeneratedSecret, setShowNewlyGeneratedSecret] = useState(false);

    const [isViewSecretOpen, setIsViewSecretOpen] = useState(false);
    const [viewingSecret, setViewingSecret] = useState<VaultSecret | null>(null);
    const [secretValue, setSecretValue] = useState("");
    const [showSecretValue, setShowSecretValue] = useState(false);
    const [isUpdateSecretOpen, setIsUpdateSecretOpen] = useState(false);
    const [updatingSecret, setUpdatingSecret] = useState<VaultSecret | null>(null);
    const [updateSecretValue, setUpdateSecretValue] = useState("");
    const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
    const [versionHistorySecret, setVersionHistorySecret] = useState<VaultSecret | null>(null);
    const [isDeleteSecretOpen, setIsDeleteSecretOpen] = useState(false);
    const [deletingSecret, setDeletingSecret] = useState<VaultSecret | null>(null);

    const [isRevokeTokenOpen, setIsRevokeTokenOpen] = useState(false);
    const [revokingToken, setRevokingToken] = useState<VaultToken | null>(null);

    const [isDeactivateClientOpen, setIsDeactivateClientOpen] = useState(false);
    const [deactivatingClient, setDeactivatingClient] = useState<VaultClient | null>(null);
    const [isDeactivateAppOpen, setIsDeactivateAppOpen] = useState(false);
    const [deactivatingApp, setDeactivatingApp] = useState<VaultClientApp | null>(null);
    const [isRevokeAllTokensOpen, setIsRevokeAllTokensOpen] = useState(false);
    const [revokeAllTokensApp, setRevokeAllTokensApp] = useState<VaultClientApp | null>(null);

    const [newClientName, setNewClientName] = useState("");
    const [newClientDescription, setNewClientDescription] = useState("");
    const [newAppName, setNewAppName] = useState("");
    const [newAppClientId, setNewAppClientId] = useState("");
    const [newAppScopes, setNewAppScopes] = useState<string[]>(["read"]);
    const [newAppLabels, setNewAppLabels] = useState("");
    const [newSecretLabel, setNewSecretLabel] = useState("");
    const [newSecretValue, setNewSecretValue] = useState("");
    const [newSecretClientId, setNewSecretClientId] = useState("");

    const [auditEventFilter, setAuditEventFilter] = useState("all");
    const [auditClientFilter, setAuditClientFilter] = useState("all");
    const [clientsFilter, setClientsFilter] = useState("all");
    const [appsFilter, setAppsFilter] = useState("all");
    const [secretsFilter, setSecretsFilter] = useState("all");
    const [tokensFilter, setTokensFilter] = useState("all");

    const ITEMS_PER_PAGE = 10;
    const [clientsPage, setClientsPage] = useState(1);
    const [appsPage, setAppsPage] = useState(1);
    const [secretsPage, setSecretsPage] = useState(1);
    const [tokensPage, setTokensPage] = useState(1);
    const [auditPage, setAuditPage] = useState(1);

    const stats = useMemo(() => ({
        totalClients: clients.length,
        activeClients: clients.filter(c => c.active).length,
        totalApps: clientApps.length,
        activeApps: clientApps.filter(a => a.active).length,
        totalSecrets: secrets.length,
        totalTokens: tokens.length,
        activeTokens: tokens.filter(t =>
            !t.revoked && ((t.isExpired === false) || (t.isExpired === undefined && new Date(t.expiresAt) > new Date()))
        ).length,
        revokedTokens: tokens.filter(t => t.revoked).length,
        recentFailures: auditLogs.filter(l => !l.success).length,
    }), [clients, clientApps, secrets, tokens, auditLogs]);

    const handleRefreshTab = async () => {
        const loadTabData = async () => {
            if (activeTab === "clients") {
                setIsLoadingClients(true);
                try {
                    const response = await listVaultClients();
                    if (response?.clients) {
                        setClients(response.clients.map(mapClientFromApi));
                    } else {
                        setClients([]);
                    }
                    toast({
                        title: "Refreshed",
                        description: "Clients data has been refreshed.",
                    });
                } catch (error: any) {
                    console.error("Failed to load vault clients", error);
                    toast({
                        title: "Unable to refresh clients",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingClients(false);
                }
            } else if (activeTab === "apps") {
                setIsLoadingApps(true);
                try {
                    const response = await listVaultApps();
                    if (response?.applications) {
                        const mappedApps = response.applications.map((app) => {
                            const matchingClient = clients.find(c => c.name === app.client_name);
                            return {
                                id: generateLocalId(),
                                clientId: matchingClient?.id || app.client_name,
                                clientName: app.client_name,
                                name: app.app_name,
                                clientSecret: app.app_secret ? app.app_secret : "••••••••••••••••",
                                scopes: apiToUiScopes(app.scopes || []),
                                allowedLabels: app.allowed_labels || [],
                                active: app.enabled,
                                createdAt: app.created_at,
                                updatedAt: app.updated_at || app.created_at,
                                activeTokens: (app as any).active_tokens || 0,
                                appId: (app as any).app_id || app.app_name,
                            };
                        });
                        setClientApps(mappedApps);
                    } else {
                        setClientApps([]);
                    }
                    toast({
                        title: "Refreshed",
                        description: "Apps data has been refreshed.",
                    });
                } catch (error: any) {
                    console.error("Failed to load vault apps", error);
                    toast({
                        title: "Unable to refresh apps",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingApps(false);
                }
            } else if (activeTab === "secrets") {
                setIsLoadingSecrets(true);
                try {
                    const response = await listVaultSecrets();
                    if (response?.secrets) {
                        const mappedSecrets = response.secrets.map((secret: any) => {
                            const matchingClient = clients.find(c => c.name === secret.clientName);
                            return {
                                id: generateLocalId(),
                                clientId: matchingClient?.id || secret.clientName,
                                clientName: secret.clientName,
                                label: secret.label,
                                vaultPath: secret.vaultPath,
                                version: secret.version,
                                createdAt: secret.lastUpdated,
                                updatedAt: secret.lastUpdated,
                                accessCount: secret.accessCount,
                                appSecret: secret.app_secret ? secret.app_secret : "••••••••••••••••",
                            };
                        });
                        setSecrets(mappedSecrets);
                    } else {
                        setSecrets([]);
                    }
                    toast({
                        title: "Refreshed",
                        description: "Secrets data has been refreshed.",
                    });
                } catch (error: any) {
                    console.error("Failed to load vault secrets", error);
                    toast({
                        title: "Unable to refresh secrets",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingSecrets(false);
                }
            } else if (activeTab === "tokens") {
                setIsLoadingTokens(true);
                try {
                    const response = await listVaultTokens({ page: 0, size: 100 });
                    if (response?.tokens && response.tokens.length > 0) {
                        const mappedTokens = response.tokens.map((token: any) => ({
                            id: token.id?.toString() || `token-${Date.now()}`,
                            appId: token.app,
                            appName: token.app,
                            clientName: token.client,
                            token: token.token,
                            expiresAt: token.expiresAt,
                            revoked: token.isRevoked,
                            createdAt: token.created,
                            lastUsedAt: token.lastUsed === "Never" ? null : token.lastUsed,
                            sourceIp: token.sourceIp || "Not used",
                            isExpired: token.isExpired,
                            status: token.status,
                        }));
                        setTokens(mappedTokens);
                    } else {
                        setTokens([]);
                    }
                    toast({
                        title: "Refreshed",
                        description: "Tokens data has been refreshed.",
                    });
                } catch (error: any) {
                    console.error("Failed to load vault tokens", error);
                    toast({
                        title: "Unable to refresh tokens",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingTokens(false);
                }
            } else if (activeTab === "audit") {
                setIsLoadingAuditLogs(true);
                try {
                    const response = await listVaultAuditLogs({ page: 0, size: 50 });
                    if (response?.items) {
                        const mappedLogs = response.items.map((log: any, idx: number) => ({
                            id: log.id?.toString() || `audit-${idx}`,
                            timestamp: log.timestamp,
                            eventType: log.action?.toLowerCase().replace(/([A-Z])/g, "_$1") || "unknown",
                            actorId: "system",
                            actorType: "admin" as const,
                            clientId: log.clientId || log.clientName,
                            clientName: log.clientName,
                            appId: log.appName ? log.appName : null,
                            appName: log.appName || null,
                            secretLabel: log.resourcePath || null,
                            vaultPath: log.resourcePath || null,
                            success: log.success,
                            sourceIp: log.ipAddress || "—",
                            additionalData: log.errorMessage || "{}",
                            requestId: log.requestId,
                            userAgent: log.userAgent,
                            metadata: log.metadata,
                            principalName: log.principalName
                        }));
                        setAuditLogs(mappedLogs);
                    } else {
                        setAuditLogs([]);
                    }
                    toast({
                        title: "Refreshed",
                        description: "Audit logs have been refreshed.",
                    });
                } catch (error: any) {
                    console.error("Failed to load audit logs", error);
                    toast({
                        title: "Unable to refresh logs",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingAuditLogs(false);
                }
            } else if (activeTab === "overview") {
                try {
                    const overviewResponse = await getVaultOverview();
                    console.log("Overview API Response:", overviewResponse);
                    toast({
                        title: "Refreshed",
                        description: "Overview data has been refreshed.",
                    });
                } catch (error: any) {
                    console.error("Failed to load overview data", error);
                    toast({
                        title: "Unable to refresh overview",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                }
            }
        };
        await loadTabData();
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: "Copied",
            description: `${label} copied to clipboard.`
        });
    };


    const handleCreateClient = async () => {
        if (!newClientName.trim()) {
            toast({
                title: "Client name required",
                description: "Enter a name before creating a client.",
                variant: "destructive",
            });
            return;
        }

        setIsSavingClient(true);
        try {
            const response = await createVaultClient({
                name: newClientName.trim(),
                description: newClientDescription.trim() || undefined,
            });

            const createdClient = mapClientFromApi(response);
            setClients((prev) => [createdClient, ...prev]);

            toast({
                title: "Client created",
                description: `Client "${createdClient.name}" has been created. Now create apps under this client.`,
            });
            setIsCreateClientOpen(false);
            setNewClientName("");
            setNewClientDescription("");
        } catch (error: any) {
            console.error("Failed to create vault client", error);
            toast({
                title: "Client creation failed",
                description: typeof error === "string" ? error : "Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsSavingClient(false);
        }
    };

    const handleCreateApp = async () => {
        if (!newAppClientId || !newAppName.trim()) {
            toast({
                title: "Missing details",
                description: "Select a client and provide an app name.",
                variant: "destructive",
            });
            return;
        }

        const selectedClient = clients.find((c) => c.id === newAppClientId);
        const allowedLabels = newAppLabels
            .split(",")
            .map((label) => label.trim())
            .filter(Boolean);

        setIsSavingApp(true);
        try {
            const response = await createVaultApp(selectedClient?.name || newAppClientId, {
                appName: newAppName.trim(),
                description: selectedClient?.description,
                scopes: uiToApiScopes(newAppScopes),
                allowedLabels,
            });

            const app: VaultClientApp = {
                id: generateLocalId(),
                clientId: selectedClient?.id || newAppClientId,
                clientName: response.client_name || selectedClient?.name || newAppClientId,
                name: response.app_name,
                clientSecret: response.app_secret ? response.app_secret : "••••••••••••••••",
                scopes: apiToUiScopes(response.scopes),
                allowedLabels: response.allowed_labels,
                active: response.enabled,
                createdAt: response.created_at,
                updatedAt: response.created_at,
                activeTokens: 0,
                appId: response.app_id || response.app_name,
            };

            setClientApps((prev) => [app, ...prev]);
            setClients((prev) => prev.map((client) => client.id === app.clientId ? { ...client, appsCount: client.appsCount + 1 } : client));
            setGeneratedCredentials({
                clientId: app.clientId,
                clientSecret: app.clientSecret,
                clientName: `${app.clientName} / ${app.name}`,
                appId: app.appId,
            });

            toast({
                title: "App created",
                description: `App "${app.name}" registered under ${app.clientName}. Save the secret now – it is shown only once.`,
            });

            setIsCreateAppOpen(false);
            setIsCredentialsDialogOpen(true);
            setShowGeneratedSecret(false);
            setNewAppName("");
            setNewAppClientId("");
            setNewAppScopes(["read"]);
            setNewAppLabels("");
        } catch (error: any) {
            console.error("Failed to create vault app", error);
            toast({
                title: "App creation failed",
                description: typeof error === "string" ? error : "Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsSavingApp(false);
        }
    };

    const handleCreateSecret = async () => {
        if (!newSecretClientId || !newSecretLabel.trim() || !newSecretValue.trim()) {
            toast({
                title: "Missing details",
                description: "Select a client, add a label, and provide a secret value.",
                variant: "destructive",
            });
            return;
        }

        const parsedValue = (() => {
            try {
                return JSON.parse(newSecretValue);
            } catch {
                return { value: newSecretValue };
            }
        })();

        setIsSavingSecret(true);
        try {
            const response = await createVaultSecret({
                clientName: newSecretClientId,
                label: newSecretLabel.trim(),
                description: undefined,
                data: parsedValue,
            });

            const secret: VaultSecret = {
                id: generateLocalId(),
                clientId: newSecretClientId,
                clientName: response.clientName,
                label: response.label,
                vaultPath: response.vaultPath,
                version: response.version,
                createdAt: response.lastUpdated,
                updatedAt: response.lastUpdated,
                accessCount: response.accessCount,
            };

            setSecrets((prev) => [secret, ...prev]);
            setClients((prev) => prev.map((client) => client.id === secret.clientId ? { ...client, secretsCount: client.secretsCount + 1 } : client));
            toast({
                title: "Secret stored",
                description: `Secret "${secret.label}" has been stored for ${secret.clientName}.`,
            });
            setIsCreateSecretOpen(false);
            setNewSecretLabel("");
            setNewSecretValue("");
            setNewSecretClientId("");
        } catch (error: any) {
            console.error("Failed to create vault secret", error);
            toast({
                title: "Secret creation failed",
                description: typeof error === "string" ? error : "Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsSavingSecret(false);
        }
    };


    const handleRevokeAllExpired = () => {
        const expiredCount = tokens.filter(t =>
            !t.revoked && (t.isExpired === true || (t.isExpired === undefined && new Date(t.expiresAt) < new Date()))
        ).length;
        toast({
            title: "Expired Tokens Revoked",
            description: `${expiredCount} expired token(s) have been revoked.`
        });
    };

    const openEditClient = (client: VaultClient) => {
        setEditingClient(client);
        setEditClientName(client.name);
        setEditClientDescription(client.description);
        setIsEditClientOpen(true);
    };

    const handleEditClient = async () => {
        if (editingClient && editClientName.trim()) {
            try {
                await updateVaultClient(editingClient.name, {
                    name: editClientName,
                    description: editClientDescription,
                });

                setClients((prev) => prev.map((client) =>
                    client.id === editingClient.id
                        ? {
                            ...client,
                            name: editClientName,
                            description: editClientDescription,
                            updatedAt: new Date().toISOString(),
                        }
                        : client
                ));

                toast({
                    title: "Client Updated",
                    description: `Client "${editClientName}" has been updated successfully.`
                });
                setIsEditClientOpen(false);
                setEditingClient(null);
            } catch (error: any) {
                console.error("Failed to update client", error);
                toast({
                    title: "Update failed",
                    description: error?.message || "Failed to update client",
                    variant: "destructive"
                });
            }
        }
    };

    const openDeleteClient = (client: VaultClient) => {
        setDeletingClient(client);
        setIsDeleteClientOpen(true);
    };

    const handleDeleteClient = async () => {
        if (deletingClient) {
            try {
                await deleteVaultClient(deletingClient.name);
                setClients((prev) => prev.filter((c) => c.id !== deletingClient.id));
                const appsCount = clientApps.filter(a => a.clientId === deletingClient.id).length;
                toast({
                    title: "Client Deleted",
                    description: `Client "${deletingClient.name}" and its ${appsCount} app(s) have been permanently deleted.`,
                    variant: "destructive"
                });
                setIsDeleteClientOpen(false);
                setDeletingClient(null);
            } catch (error: any) {
                console.error("Failed to delete client", error);
                toast({
                    title: "Delete failed",
                    description: error?.message || "Failed to delete client",
                    variant: "destructive"
                });
            }
        }
    };

    const openDeactivateClient = (client: VaultClient) => {
        setDeactivatingClient(client);
        setIsDeactivateClientOpen(true);
    };

    const handleConfirmDeactivateClient = async () => {
        if (deactivatingClient) {
            try {
                if (deactivatingClient.active) {
                    await disableVaultClient(deactivatingClient.name);
                } else {
                    await enableVaultClient(deactivatingClient.name);
                }

                setClients((prev) => prev.map((client) =>
                    client.id === deactivatingClient.id ? { ...client, active: !client.active } : client
                ));

                const appsCount = clientApps.filter(a => a.clientId === deactivatingClient.id && a.active).length;
                const tokensCount = tokens.filter(t =>
                    clientApps.find(a => a.id === t.appId)?.clientId === deactivatingClient.id && !t.revoked
                ).length;
                toast({
                    title: deactivatingClient.active ? "Client Deactivated" : "Client Activated",
                    description: deactivatingClient.active
                        ? `Client "${deactivatingClient.name}" and ${appsCount} app(s) have been deactivated. ${tokensCount} token(s) revoked.`
                        : `Client "${deactivatingClient.name}" has been activated.`
                });
                setIsDeactivateClientOpen(false);
                setDeactivatingClient(null);
            } catch (error: any) {
                console.error("Failed to toggle client status", error);
                toast({
                    title: "Operation failed",
                    description: error?.message || "Failed to toggle client status",
                    variant: "destructive"
                });
            }
        }
    };

    // Client App Handlers
    const openEditApp = (app: VaultClientApp) => {
        setEditingApp(app);
        setEditAppName(app.name);
        setEditAppLabels(app.allowedLabels.join(", "));
        setEditAppScopes([...app.scopes]);
        setIsEditAppOpen(true);
    };

    const handleEditApp = async () => {
        if (editingApp && editAppName.trim()) {
            try {
                const parsedLabels = editAppLabels.split(",").map(s => s.trim()).filter(s => s.length > 0);

                await updateVaultAppScopes(editingApp.name, {
                    name: editAppName,
                    scopes: editAppScopes.map(s => s.includes(":") ? s : `secret:${s}`),
                    allowedLabels: parsedLabels
                });

                const mappedScopes = editAppScopes.filter((s) => s === "read" || s === "write" || s === "delete") as ("read" | "write" | "delete")[];

                setClientApps((prev) => prev.map((app) =>
                    app.id === editingApp.id
                        ? {
                            ...app,
                            name: editAppName,
                            scopes: mappedScopes,
                            allowedLabels: parsedLabels
                        }
                        : app
                ));
                toast({
                    title: "App Updated",
                    description: `App "${editAppName}" has been updated successfully.`
                });
                setIsEditAppOpen(false);
                setEditingApp(null);
            } catch (error: any) {
                console.error("Failed to update app", error);
                toast({
                    title: "Update failed",
                    description: error?.message || "Failed to update app",
                    variant: "destructive"
                });
            }
        }
    };

    const openDeleteApp = (app: VaultClientApp) => {
        setDeletingApp(app);
        setIsDeleteAppOpen(true);
    };

    const handleDeleteApp = async () => {
        if (deletingApp) {
            try {
                await deleteVaultApp(deletingApp.name);
                setClientApps((prev) => prev.filter((a) => a.id !== deletingApp.id));
                toast({
                    title: "App Deleted",
                    description: `App "${deletingApp.name}" and all its tokens have been permanently deleted.`,
                    variant: "destructive"
                });
                setIsDeleteAppOpen(false);
                setDeletingApp(null);
            } catch (error: any) {
                console.error("Failed to delete app", error);
                toast({
                    title: "Delete failed",
                    description: error?.message || "Failed to delete app",
                    variant: "destructive"
                });
            }
        }
    };

    const openRegenerateSecret = (app: VaultClientApp) => {
        setRegeneratingApp(app);
        setIsRegenerateSecretOpen(true);
        setNewlyGeneratedSecret("");
        setShowNewlyGeneratedSecret(false);
    };

    const handleConfirmRegenerateSecret = async () => {
        if (regeneratingApp) {
            try {
                const response = await regenerateVaultAppSecret(regeneratingApp.name);
                const secret = response.app_secret ? response.app_secret : "••••••••••••••••";
                setNewlyGeneratedSecret(secret);
                toast({
                    title: "Secret Regenerated",
                    description: `New client secret generated for "${regeneratingApp.name}". ${response.warning || ""}`
                });
            } catch (error: any) {
                console.error("Failed to regenerate secret", error);
                toast({
                    title: "Regeneration failed",
                    description: error?.message || "Failed to regenerate secret",
                    variant: "destructive"
                });
            }
        }
    };



    const openDeactivateApp = (app: VaultClientApp) => {
        setDeactivatingApp(app);
        setIsDeactivateAppOpen(true);
    };

    const handleConfirmDeactivateApp = async () => {
        if (deactivatingApp) {
            try {
                if (deactivatingApp.active) {
                    await disableVaultApp(deactivatingApp.name);
                } else {
                    await enableVaultApp(deactivatingApp.name);
                }

                setClientApps((prev) => prev.map((app) =>
                    app.id === deactivatingApp.id ? { ...app, active: !app.active } : app
                ));

                const tokensCount = tokens.filter((t) => t.appId === deactivatingApp.id && !t.revoked).length;
                toast({
                    title: deactivatingApp.active ? "App Deactivated" : "App Activated",
                    description: deactivatingApp.active
                        ? `App "${deactivatingApp.name}" has been deactivated. ${tokensCount} active token(s) have been revoked.`
                        : `App "${deactivatingApp.name}" has been activated.`
                });
                setIsDeactivateAppOpen(false);
                setDeactivatingApp(null);
            } catch (error: any) {
                console.error("Failed to toggle app status", error);
                toast({
                    title: "Operation failed",
                    description: error?.message || "Failed to toggle app status",
                    variant: "destructive"
                });
            }
        }
    };

    const openRevokeAllTokens = (app: VaultClientApp) => {
        setRevokeAllTokensApp(app);
        setIsRevokeAllTokensOpen(true);
    };

    const handleConfirmRevokeAllTokens = async () => {
        if (revokeAllTokensApp) {
            try {
                const response = await revokeAppAllTokens(revokeAllTokensApp.clientName, revokeAllTokensApp.name);

                setTokens((prev) => prev.map((t) =>
                    t.appId === revokeAllTokensApp.id ? { ...t, revoked: true } : t
                ));

                setClientApps((prev) => prev.map((a) =>
                    a.id === revokeAllTokensApp.id ? { ...a, activeTokens: 0 } : a
                ));

                toast({
                    title: "All Tokens Revoked",
                    description: `${response.tokensRevoked} token(s) for "${revokeAllTokensApp.name}" have been revoked.`
                });
                setIsRevokeAllTokensOpen(false);
                setRevokeAllTokensApp(null);
            } catch (error: any) {
                console.error("Failed to revoke all tokens", error);
                toast({
                    title: "Revoke failed",
                    description: error?.message || "Failed to revoke tokens",
                    variant: "destructive"
                });
            }
        }
    };

    const openViewSecret = async (secret: VaultSecret) => {
        setViewingSecret(secret);
        setShowSecretValue(false);

        try {
            const response = await getVaultSecretValue(secret.clientName, secret.vaultPath);
            const secretData = typeof response === 'string' ? response : JSON.stringify(response, null, 2);
            setSecretValue(secretData);
        } catch (error: any) {
            console.error("Failed to fetch secret value", error);
            toast({
                title: "Failed to fetch secret",
                description: typeof error === "string" ? error : "Unable to retrieve secret value.",
                variant: "destructive",
            });
            setSecretValue("");
        }

        setIsViewSecretOpen(true);
    };

    const openUpdateSecret = (secret: VaultSecret) => {
        setUpdatingSecret(secret);
        setUpdateSecretValue("");
        setIsUpdateSecretOpen(true);
    };

    const handleUpdateSecret = async () => {
        if (updatingSecret && updateSecretValue.trim()) {
            try {
                let secretData: Record<string, unknown> = {};
                try {
                    secretData = JSON.parse(updateSecretValue);
                } catch {
                    secretData = { value: updateSecretValue };
                }

                await updateVaultSecret(updatingSecret.clientName, updatingSecret.label, {
                    clientName: updatingSecret.clientName,
                    label: updatingSecret.label,
                    data: secretData
                });

                setSecrets((prev) => prev.map((s) =>
                    s.id === updatingSecret.id
                        ? { ...s, version: s.version + 1, updatedAt: new Date().toISOString() }
                        : s
                ));

                toast({
                    title: "Secret Updated",
                    description: `Secret "${updatingSecret.label}" has been updated to version ${updatingSecret.version + 1}.`
                });
                setIsUpdateSecretOpen(false);
                setUpdatingSecret(null);
            } catch (error: any) {
                console.error("Failed to update secret", error);
                toast({
                    title: "Update failed",
                    description: error?.message || "Failed to update secret",
                    variant: "destructive"
                });
            }
        }
    };

    const openVersionHistory = (secret: VaultSecret) => {
        setVersionHistorySecret(secret);
        setIsVersionHistoryOpen(true);
    };

    const openDeleteSecret = (secret: VaultSecret) => {
        setDeletingSecret(secret);
        setIsDeleteSecretOpen(true);
    };

    const handleDeleteSecret = async () => {
        if (deletingSecret) {
            try {
                await deleteVaultSecret(deletingSecret.clientName, deletingSecret.label);
                setSecrets((prev) => prev.filter((s) => s.id !== deletingSecret.id));
                toast({
                    title: "Secret Deleted",
                    description: `Secret "${deletingSecret.label}" has been permanently deleted from the vault.`,
                    variant: "destructive"
                });
                setIsDeleteSecretOpen(false);
                setDeletingSecret(null);
            } catch (error: any) {
                console.error("Failed to delete secret", error);
                toast({
                    title: "Delete failed",
                    description: error?.message || "Failed to delete secret",
                    variant: "destructive"
                });
            }
        }
    };

    const openRevokeToken = (token: VaultToken) => {
        setRevokingToken(token);
        setIsRevokeTokenOpen(true);
    };

    const handleConfirmRevokeToken = async () => {
        if (revokingToken) {
            try {
                await revokeVaultTokenById(parseInt(revokingToken.id));
                toast({
                    title: "Token Revoked",
                    description: `Token for "${revokingToken.appName}" has been revoked and can no longer be used.`
                });
                setTokens(prev => prev.map(t =>
                    t.id === revokingToken.id ? { ...t, revoked: true } : t
                ));
            } catch (error: any) {
                toast({
                    title: "Failed to revoke token",
                    description: typeof error === "string" ? error : "Please try again.",
                    variant: "destructive"
                });
            } finally {
                setIsRevokeTokenOpen(false);
                setRevokingToken(null);
            }
        }
    };

    const handleExportAuditLogs = () => {
        const logData = JSON.stringify(filteredAuditLogs, null, 2);
        const blob = new Blob([logData], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `vault-audit-logs-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast({
            title: "Logs Exported",
            description: `${filteredAuditLogs.length} audit log entries exported.`
        });
    };

    const formatLocalDate = (dateString: string | null): string => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "Invalid Date";
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        } catch (e) {
            return "Invalid Date";
        }
    };

    const formatLocalDateTime = (dateString: string | null): string => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "Invalid Date";
            return date.toLocaleString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (e) {
            return "Invalid Date";
        }
    };

    const formatLocalTime = (dateString: string | null): string => {
        if (!dateString) return "Never";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "Invalid Date";
            return date.toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (e) {
            return "Invalid Date";
        }
    };

    const getUserAgentDisplay = (userAgent: string | undefined): string => {
        if (!userAgent) return "";
        const browserKeywords = ["Chrome", "Firefox", "Safari", "Edge", "Opera", "IE", "Trident", "Gecko", "WebKit"];
        const hasBrowser = browserKeywords.some(keyword => userAgent.includes(keyword));
        return hasBrowser ? "via Browser" : userAgent;
    };

    const getEventTypeBadge = (eventType: string, success: boolean) => {
        if (!success) {
            return <Badge variant="destructive" className="gap-1"><XCircle className="w-3 h-3" />Failed</Badge>;
        }

        const eventStyles: Record<string, { bg: string; icon: JSX.Element }> = {
            "secret_read": { bg: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300", icon: <Eye className="w-3 h-3" /> },
            "secret_update": { bg: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300", icon: <Edit className="w-3 h-3" /> },
            "secret_create": { bg: "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300", icon: <Plus className="w-3 h-3" /> },
            "secret_delete": { bg: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300", icon: <Trash2 className="w-3 h-3" /> },
            "token_create": { bg: "bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300", icon: <Key className="w-3 h-3" /> },
            "token_revoke": { bg: "bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300", icon: <Ban className="w-3 h-3" /> },
            "client_update": { bg: "bg-slate-100 text-slate-800 dark:bg-slate-900/50 dark:text-slate-300", icon: <Users className="w-3 h-3" /> },
            "app_create": { bg: "bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300", icon: <AppWindow className="w-3 h-3" /> },
            "auth_failed": { bg: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300", icon: <AlertTriangle className="w-3 h-3" /> },
        };

        const style = eventStyles[eventType] || { bg: "bg-gray-100 text-gray-800", icon: <Activity className="w-3 h-3" /> };
        return (
            <Badge variant="secondary" className={`gap-1 ${style.bg}`}>
                {style.icon}
                {eventType.replace(/_/g, " ")}
            </Badge>
        );
    };

    const getExpiryStatus = (expiresAt: string, revoked: boolean, isExpired?: boolean, createdAt?: string) => {
        if (revoked) {
            return <Badge variant="destructive" className="gap-1"><XCircle className="w-3 h-3" />Revoked</Badge>;
        }

        if (isExpired === true) {
            return <Badge variant="secondary" className="gap-1 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"><Clock className="w-3 h-3" />Expired</Badge>;
        }

        try {
            const expiry = new Date(expiresAt);
            const now = createdAt ? new Date(createdAt) : new Date();
            if (isNaN(expiry.getTime())) {
                return <Badge variant="secondary" className="gap-1 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"><Clock className="w-3 h-3" />Invalid Date</Badge>;
            }

            const hoursLeft = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60);

            if (hoursLeft < 0 && isExpired !== false) {
                return <Badge variant="secondary" className="gap-1 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"><Clock className="w-3 h-3" />Expired</Badge>;
            }
            if (hoursLeft < 1) {
                return <Badge variant="secondary" className="gap-1 bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300"><AlertTriangle className="w-3 h-3" />{Math.round(hoursLeft * 60)}m left</Badge>;
            }
            if (hoursLeft < 6) {
                return <Badge variant="secondary" className="gap-1 bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"><Clock className="w-3 h-3" />{Math.round(hoursLeft)}h left</Badge>;
            }
            if (hoursLeft < 24) {
                return <Badge variant="secondary" className="gap-1 bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"><Clock className="w-3 h-3" />{Math.round(hoursLeft)}h left</Badge>;
            }
            return <Badge variant="secondary" className="gap-1 bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300"><CheckCircle2 className="w-3 h-3" />Active</Badge>;
        } catch (e) {
            return <Badge variant="secondary" className="gap-1 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"><Clock className="w-3 h-3" />Unknown</Badge>;
        }
    };

    const filteredClients = useMemo(() => {
        return clients.filter(client => {
            if (clientsFilter === "active" && !client.active) return false;
            if (clientsFilter === "inactive" && client.active) return false;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                return client.name.toLowerCase().includes(term) || client.description.toLowerCase().includes(term);
            }
            return true;
        });
    }, [clients, clientsFilter, searchTerm]);

    const filteredApps = useMemo(() => {
        return clientApps.filter(app => {
            if (appsFilter === "active" && !app.active) return false;
            if (appsFilter === "inactive" && app.active) return false;
            if (appsFilter !== "all" && appsFilter !== "active" && appsFilter !== "inactive" && app.clientId !== appsFilter) return false;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                return app.name.toLowerCase().includes(term) || app.clientName.toLowerCase().includes(term);
            }
            return true;
        });
    }, [appsFilter, clientApps, searchTerm]);

    const filteredSecrets = useMemo(() => {
        return secrets.filter(secret => {
            if (secretsFilter !== "all" && secret.clientId !== secretsFilter) return false;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                return secret.label.toLowerCase().includes(term) || secret.clientName.toLowerCase().includes(term);
            }
            return true;
        });
    }, [secrets, secretsFilter, searchTerm]);

    const filteredTokens = useMemo(() => {
        return tokens.filter(token => {
            if (tokensFilter === "active" && (
                token.revoked || (token.isExpired === true || (token.isExpired === undefined && new Date(token.expiresAt) < new Date()))
            )) return false;
            if (tokensFilter === "revoked" && !token.revoked) return false;
            if (tokensFilter === "expired" && (
                token.revoked || (token.isExpired === false || (token.isExpired === undefined && new Date(token.expiresAt) >= new Date()))
            )) return false;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                return token.appName.toLowerCase().includes(term) || token.clientName.toLowerCase().includes(term) || token.sourceIp.includes(term);
            }
            return true;
        });
    }, [searchTerm, tokens, tokensFilter]);

    const filteredAuditLogs = useMemo(() => {
        return auditLogs.filter(log => {
            if (auditEventFilter !== "all" && log.eventType !== auditEventFilter) return false;
            if (auditClientFilter !== "all" && log.clientId !== auditClientFilter) return false;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                return (
                    log.clientName.toLowerCase().includes(term) ||
                    (log.appName?.toLowerCase().includes(term)) ||
                    (log.secretLabel?.toLowerCase().includes(term)) ||
                    log.sourceIp.includes(term)
                );
            }
            return true;
        });
    }, [auditClientFilter, auditEventFilter, auditLogs, searchTerm]);

    const paginatedClients = useMemo(() => {
        const start = (clientsPage - 1) * ITEMS_PER_PAGE;
        return filteredClients.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredClients, clientsPage]);

    const paginatedApps = useMemo(() => {
        const start = (appsPage - 1) * ITEMS_PER_PAGE;
        return filteredApps.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredApps, appsPage]);

    const paginatedSecrets = useMemo(() => {
        const start = (secretsPage - 1) * ITEMS_PER_PAGE;
        return filteredSecrets.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredSecrets, secretsPage]);

    const paginatedTokens = useMemo(() => {
        const start = (tokensPage - 1) * ITEMS_PER_PAGE;
        return filteredTokens.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredTokens, tokensPage]);

    const paginatedAuditLogs = useMemo(() => {
        const start = (auditPage - 1) * ITEMS_PER_PAGE;
        return filteredAuditLogs.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredAuditLogs, auditPage]);

    const renderPagination = (currentPage: number, totalItems: number, setPage: (page: number) => void) => {
        const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
        if (totalPages <= 1) return null;

        return (
            <div className="flex items-center justify-between px-4 py-3 border-t">
                <div className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </Button>
                    <span className="text-sm px-2">Page {currentPage} of {totalPages}</span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="border-b bg-card">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                                <Shield className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-semibold text-foreground">Vault Service Management</h1>
                                <p className="text-sm text-muted-foreground">Manage clients, secrets, tokens, and access policies</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Badge variant="outline" className="gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
                                <Server className="w-3.5 h-3.5" />
                                Vault Connected
                            </Badge>
                        </div>
                    </div>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <div className="border-b bg-card/50 px-6">
                    <TabsList className="h-12 bg-transparent gap-2">
                        <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white gap-2">
                            <Activity className="w-4 h-4" />
                            Overview
                        </TabsTrigger>
                        <TabsTrigger value="clients" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white gap-2">
                            <Building2 className="w-4 h-4" />
                            Clients
                        </TabsTrigger>
                        <TabsTrigger value="apps" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white gap-2">
                            <AppWindow className="w-4 h-4" />
                            Client Apps
                        </TabsTrigger>
                        <TabsTrigger value="secrets" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white gap-2">
                            <Lock className="w-4 h-4" />
                            Secrets
                        </TabsTrigger>
                        <TabsTrigger value="tokens" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white gap-2">
                            <KeyRound className="w-4 h-4" />
                            Tokens
                        </TabsTrigger>
                        <TabsTrigger value="audit" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white gap-2">
                            <ScrollText className="w-4 h-4" />
                            Audit Logs
                        </TabsTrigger>
                    </TabsList>
                </div>

                <TabsContent value="overview" className="p-6 space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                                        <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.activeClients}</p>
                                        <p className="text-xs text-muted-foreground">Active Clients</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                                        <AppWindow className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.activeApps}</p>
                                        <p className="text-xs text-muted-foreground">Active Apps</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/50 rounded-lg flex items-center justify-center">
                                        <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.totalSecrets}</p>
                                        <p className="text-xs text-muted-foreground">Secrets Stored</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
                                        <Key className="w-5 h-5 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.activeTokens}</p>
                                        <p className="text-xs text-muted-foreground">Active Tokens</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/50 rounded-lg flex items-center justify-center">
                                        <Ban className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.revokedTokens}</p>
                                        <p className="text-xs text-muted-foreground">Revoked Tokens</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-red-100 dark:bg-red-900/50 rounded-lg flex items-center justify-center">
                                        <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.recentFailures}</p>
                                        <p className="text-xs text-muted-foreground">Failed Events</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Recent Activity & Top Secrets */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base font-semibold flex items-center gap-2">
                                    <Activity className="w-4 h-4 text-blue-600" />
                                    Recent Activity
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="divide-y">
                                    {auditLogs.slice(0, 5).map((log) => (
                                        <div key={log.id} className="px-4 py-3 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                {getEventTypeBadge(log.eventType, log.success)}
                                                <div>
                                                    <p className="text-sm font-medium">{log.clientName}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {log.secretLabel || log.appName || "System"}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className="text-xs text-muted-foreground">
                                                {formatLocalTime(log.timestamp)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base font-semibold flex items-center gap-2">
                                    <Lock className="w-4 h-4 text-amber-600" />
                                    Most Accessed Secrets
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="divide-y">
                                    {[...secrets].sort((a, b) => b.accessCount - a.accessCount).slice(0, 5).map((secret) => (
                                        <div key={secret.id} className="px-4 py-3 flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-mono font-medium">{secret.label}</p>
                                                <p className="text-xs text-muted-foreground">{secret.clientName}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-semibold">{secret.accessCount.toLocaleString()}</p>
                                                <p className="text-xs text-muted-foreground">accesses</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="clients" className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search clients..."
                                    className="pl-9 w-64"
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setClientsPage(1); }}
                                />
                            </div>
                            <Select value={clientsFilter} onValueChange={(v) => { setClientsFilter(v); setClientsPage(1); }}>
                                <SelectTrigger className="w-32">
                                    <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Status</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="inactive">Inactive</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handleRefreshTab}
                                disabled={isLoadingClients}
                                className="h-10 w-10"
                            >
                                <RefreshCw className={`w-4 h-4 ${isLoadingClients ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button onClick={() => setIsCreateClientOpen(true)} className="gap-2 bg-blue-600 hover:bg-blue-700">
                                <Plus className="w-4 h-4" />
                                Create Client
                            </Button>
                        </div>
                    </div>

                    <Card>
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50">
                                        <TableHead className="font-semibold pl-4">Client Name</TableHead>
                                        <TableHead className="font-semibold">Description</TableHead>
                                        <TableHead className="font-semibold">Apps</TableHead>
                                        <TableHead className="font-semibold">Secrets</TableHead>
                                        <TableHead className="font-semibold">Status</TableHead>
                                        <TableHead className="font-semibold">Created</TableHead>
                                        <TableHead className="font-semibold pr-4">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {isLoadingClients && (
                                        <TableRow>
                                            <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                                                Loading clients...
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {!isLoadingClients && paginatedClients.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                                                No clients found. Create a client to get started.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {!isLoadingClients && paginatedClients.map((client) => (
                                        <TableRow key={client.id} className="hover:bg-muted/30">
                                            <TableCell className="pl-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                                                        <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                    </div>
                                                    <span className="font-medium">{client.name}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                                                {client.description}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="secondary">{client.appsCount}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="secondary">{client.secretsCount}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                {client.active ? (
                                                    <Badge variant="secondary" className="gap-1 bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                                                        <CheckCircle2 className="w-3 h-3" />
                                                        Active
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="secondary" className="gap-1 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                                                        <XCircle className="w-3 h-3" />
                                                        Inactive
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {formatLocalDate(client.createdAt)}
                                            </TableCell>
                                            <TableCell className="pr-4">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                                            <MoreVertical className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem className="gap-2" onClick={() => openEditClient(client)}>
                                                            <Edit className="w-4 h-4" />
                                                            Edit Client
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem className="gap-2" onClick={() => openDeactivateClient(client)}>
                                                            {client.active ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                                                            {client.active ? "Deactivate" : "Activate"}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem className="gap-2 text-red-600" onClick={() => openDeleteClient(client)}>
                                                            <Trash2 className="w-4 h-4" />
                                                            Delete Client
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {renderPagination(clientsPage, filteredClients.length, setClientsPage)}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="apps" className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search apps..."
                                    className="pl-9 w-64"
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setAppsPage(1); }}
                                />
                            </div>
                            <Select value={appsFilter} onValueChange={(v) => { setAppsFilter(v); setAppsPage(1); }}>
                                <SelectTrigger className="w-40">
                                    <SelectValue placeholder="Filter" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Apps</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="inactive">Inactive</SelectItem>
                                    {clients.map(c => (
                                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handleRefreshTab}
                                disabled={isLoadingApps}
                                className="h-10 w-10"
                            >
                                <RefreshCw className={`w-4 h-4 ${isLoadingApps ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button onClick={() => setIsCreateAppOpen(true)} className="gap-2 bg-blue-600 hover:bg-blue-700">
                                <Plus className="w-4 h-4" />
                                Create App
                            </Button>
                        </div>
                    </div>

                    <Card>
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50">
                                        <TableHead className="font-semibold pl-4">App Name</TableHead>
                                        <TableHead className="font-semibold">Client</TableHead>
                                        <TableHead className="font-semibold">Client Secret</TableHead>
                                        <TableHead className="font-semibold">Scopes</TableHead>
                                        <TableHead className="font-semibold">Allowed Labels</TableHead>
                                        <TableHead className="font-semibold">Active Tokens</TableHead>
                                        <TableHead className="font-semibold">Status</TableHead>
                                        <TableHead className="font-semibold pr-4">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {isLoadingApps && (
                                        <TableRow>
                                            <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                                                Loading apps...
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {!isLoadingApps && paginatedApps.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                                                No apps found. Create an app to get started.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {!isLoadingApps && paginatedApps.map((app) => (
                                        <TableRow key={app.id} className="hover:bg-muted/30">
                                            <TableCell className="pl-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg flex items-center justify-center">
                                                        <AppWindow className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                                    </div>
                                                    <span className="font-medium font-mono text-sm">{app.name}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm">{app.clientName}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                                                        {showSecret === app.id ? app.clientSecret : "••••••••••••••••"}
                                                    </code>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6"
                                                        onClick={() => setShowSecret(showSecret === app.id ? null : app.id)}
                                                    >
                                                        {showSecret === app.id ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6"
                                                        onClick={() => copyToClipboard(app.clientSecret, "Client secret")}
                                                    >
                                                        <Copy className="w-3 h-3" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    {app.scopes.map(scope => (
                                                        <Badge
                                                            key={scope}
                                                            variant="outline"
                                                            className={`text-xs ${scope === "delete" ? "border-red-200 text-red-700 dark:border-red-800 dark:text-red-300" :
                                                                scope === "write" ? "border-amber-200 text-amber-700 dark:border-amber-800 dark:text-amber-300" :
                                                                    "border-blue-200 text-blue-700 dark:border-blue-800 dark:text-blue-300"
                                                                }`}
                                                        >
                                                            {scope}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-wrap gap-1 max-w-[150px]">
                                                    {app.allowedLabels.slice(0, 3).map(label => (
                                                        <Badge key={label} variant="secondary" className="text-xs">{label}</Badge>
                                                    ))}
                                                    {app.allowedLabels.length > 3 && (
                                                        <Badge variant="secondary" className="text-xs">+{app.allowedLabels.length - 3}</Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="secondary">{app.activeTokens}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                {app.active ? (
                                                    <Badge variant="secondary" className="gap-1 bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                                                        <CheckCircle2 className="w-3 h-3" />
                                                        Active
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="secondary" className="gap-1 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                                                        <XCircle className="w-3 h-3" />
                                                        Inactive
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="pr-4">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                                            <MoreVertical className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem className="gap-2" onClick={() => { setViewingApp(app); setIsViewCredentialsOpen(true); setShowViewingSecret(false); }}>
                                                            <Key className="w-4 h-4" />
                                                            View Credentials
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem className="gap-2" onClick={() => openEditApp(app)}>
                                                            <Edit className="w-4 h-4" />
                                                            Edit App
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem className="gap-2" onClick={() => openRegenerateSecret(app)}>
                                                            <RefreshCw className="w-4 h-4" />
                                                            Regenerate Secret
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem className="gap-2" onClick={() => openRevokeAllTokens(app)}>
                                                            <Ban className="w-4 h-4" />
                                                            Revoke All Tokens
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem className="gap-2" onClick={() => openDeactivateApp(app)}>
                                                            {app.active ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                                                            {app.active ? "Deactivate" : "Activate"}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem className="gap-2 text-red-600" onClick={() => openDeleteApp(app)}>
                                                            <Trash2 className="w-4 h-4" />
                                                            Delete App
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {renderPagination(appsPage, filteredApps.length, setAppsPage)}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="secrets" className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search secrets by label..."
                                    className="pl-9 w-64"
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setSecretsPage(1); }}
                                />
                            </div>
                            <Select value={secretsFilter} onValueChange={(v) => { setSecretsFilter(v); setSecretsPage(1); }}>
                                <SelectTrigger className="w-48">
                                    <SelectValue placeholder="Filter by client" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Clients</SelectItem>
                                    {clients.map(c => (
                                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handleRefreshTab}
                                disabled={isLoadingSecrets}
                                className="h-10 w-10"
                            >
                                <RefreshCw className={`w-4 h-4 ${isLoadingSecrets ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button onClick={() => setIsCreateSecretOpen(true)} className="gap-2 bg-blue-600 hover:bg-blue-700">
                                <Plus className="w-4 h-4" />
                                Create Secret
                            </Button>
                        </div>
                    </div>

                    <Card>
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50">
                                        <TableHead className="font-semibold pl-4">Label</TableHead>
                                        <TableHead className="font-semibold">Client</TableHead>
                                        <TableHead className="font-semibold">Secret</TableHead>
                                        <TableHead className="font-semibold">Vault Path</TableHead>
                                        <TableHead className="font-semibold">Version</TableHead>
                                        <TableHead className="font-semibold">Access Count</TableHead>
                                        <TableHead className="font-semibold">Last Updated</TableHead>
                                        <TableHead className="font-semibold pr-4">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {isLoadingSecrets && (
                                        <TableRow>
                                            <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                                                Loading secrets...
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {!isLoadingSecrets && paginatedSecrets.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                                                No secrets found. Create a secret to get started.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {!isLoadingSecrets && paginatedSecrets.map((secret) => (
                                        <TableRow key={secret.id} className="hover:bg-muted/30">
                                            <TableCell className="pl-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/50 rounded-lg flex items-center justify-center">
                                                        <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                                    </div>
                                                    <span className="font-medium font-mono text-sm">{secret.label}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm">{secret.clientName}</TableCell>
                                            <TableCell>
                                                <code className="text-xs bg-muted px-2 py-1 rounded font-mono text-muted-foreground">
                                                    {secret.appSecret || "••••••••••••••••"}
                                                </code>
                                            </TableCell>
                                            <TableCell>
                                                <code className="text-xs bg-muted px-2 py-1 rounded font-mono text-muted-foreground">
                                                    {secret.vaultPath.trim()}
                                                </code>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="font-mono">v{secret.version}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm font-medium">{secret.accessCount.toLocaleString()}</span>
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {formatLocalDate(secret.updatedAt)}
                                            </TableCell>
                                            <TableCell className="pr-4">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                                            <MoreVertical className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem className="gap-2" onClick={() => openViewSecret(secret)}>
                                                            <Eye className="w-4 h-4" />
                                                            View Secret
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem className="gap-2" onClick={() => openUpdateSecret(secret)}>
                                                            <Edit className="w-4 h-4" />
                                                            Update Secret
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem className="gap-2" onClick={() => openVersionHistory(secret)}>
                                                            <History className="w-4 h-4" />
                                                            Version History
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem className="gap-2 text-red-600" onClick={() => openDeleteSecret(secret)}>
                                                            <Trash2 className="w-4 h-4" />
                                                            Delete Secret
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {renderPagination(secretsPage, filteredSecrets.length, setSecretsPage)}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="tokens" className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search by app or IP..."
                                    className="pl-9 w-64"
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setTokensPage(1); }}
                                />
                            </div>
                            <Select value={tokensFilter} onValueChange={(v) => { setTokensFilter(v); setTokensPage(1); }}>
                                <SelectTrigger className="w-40">
                                    <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Tokens</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="revoked">Revoked</SelectItem>
                                    <SelectItem value="expired">Expired</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handleRefreshTab}
                                disabled={isLoadingTokens}
                                className="h-10 w-10"
                            >
                                <RefreshCw className={`w-4 h-4 ${isLoadingTokens ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button
                                variant="outline"
                                className="gap-2 text-orange-600 border-orange-200 hover:bg-orange-50 dark:text-orange-400 dark:border-orange-800 dark:hover:bg-orange-950"
                                onClick={handleRevokeAllExpired}
                            >
                                <Ban className="w-4 h-4" />
                                Revoke All Expired
                            </Button>
                        </div>
                    </div>

                    <Card>
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50">
                                        <TableHead className="font-semibold pl-4">App</TableHead>
                                        <TableHead className="font-semibold">Client</TableHead>
                                        <TableHead className="font-semibold">Token (Preview)</TableHead>
                                        <TableHead className="font-semibold">Source IP</TableHead>
                                        <TableHead className="font-semibold">Status</TableHead>
                                        <TableHead className="font-semibold">Created</TableHead>
                                        <TableHead className="font-semibold">Last Used</TableHead>
                                        <TableHead className="font-semibold pr-4">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {isLoadingTokens && (
                                        <TableRow>
                                            <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                                                Loading tokens...
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {!isLoadingTokens && paginatedTokens.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                                                No tokens yet. Issue one from the Apps tab.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {!isLoadingTokens && paginatedTokens.map((token) => (
                                        <TableRow key={token.id} className="hover:bg-muted/30">
                                            <TableCell className="pl-4">
                                                <span className="font-medium font-mono text-sm">{token.appName}</span>
                                            </TableCell>
                                            <TableCell className="text-sm">{token.clientName}</TableCell>
                                            <TableCell>
                                                <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                                                    {token.token}
                                                </code>
                                            </TableCell>
                                            <TableCell>
                                                <span className="font-mono text-sm">{token.sourceIp}</span>
                                            </TableCell>
                                            <TableCell>
                                                {getExpiryStatus(token.expiresAt, token.revoked, token.isExpired, token.createdAt)}
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {formatLocalDateTime(token.createdAt)}
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {token.lastUsedAt ? formatLocalTime(token.lastUsedAt) : "Never"}
                                            </TableCell>
                                            <TableCell className="pr-4">
                                                {!token.revoked && ((token.isExpired === false) || (token.isExpired === undefined && new Date(token.expiresAt) > new Date())) && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-950"
                                                        onClick={() => openRevokeToken(token)}
                                                    >
                                                        <Ban className="w-3 h-3" />
                                                        Revoke
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {renderPagination(tokensPage, filteredTokens.length, setTokensPage)}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Audit Logs Tab */}
                <TabsContent value="audit" className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search logs..."
                                    className="pl-9 w-64"
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setAuditPage(1); }}
                                />
                            </div>
                            <Select value={auditEventFilter} onValueChange={(v) => { setAuditEventFilter(v); setAuditPage(1); }}>
                                <SelectTrigger className="w-44">
                                    <SelectValue placeholder="Event Type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Events</SelectItem>
                                    <SelectItem value="secret_read">Secret Read</SelectItem>
                                    <SelectItem value="secret_update">Secret Update</SelectItem>
                                    <SelectItem value="secret_create">Secret Create</SelectItem>
                                    <SelectItem value="secret_delete">Secret Delete</SelectItem>
                                    <SelectItem value="token_create">Token Create</SelectItem>
                                    <SelectItem value="token_revoke">Token Revoke</SelectItem>
                                    <SelectItem value="auth_failed">Auth Failed</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={auditClientFilter} onValueChange={(v) => { setAuditClientFilter(v); setAuditPage(1); }}>
                                <SelectTrigger className="w-48">
                                    <SelectValue placeholder="Client" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Clients</SelectItem>
                                    {clients.map(c => (
                                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handleRefreshTab}
                                disabled={isLoadingAuditLogs}
                                className="h-10 w-10"
                            >
                                <RefreshCw className={`w-4 h-4 ${isLoadingAuditLogs ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button variant="outline" className="gap-2" onClick={handleExportAuditLogs}>
                                <Download className="w-4 h-4" />
                                Export Logs
                            </Button>
                        </div>
                    </div>

                    <Card>
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50">
                                        <TableHead className="font-semibold pl-4">Timestamp</TableHead>
                                        <TableHead className="font-semibold">Event</TableHead>
                                        <TableHead className="font-semibold">User</TableHead>
                                        <TableHead className="font-semibold">Client / App</TableHead>
                                        <TableHead className="font-semibold">Target</TableHead>
                                        <TableHead className="font-semibold">Source IP</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paginatedAuditLogs.map((log) => (
                                        <TableRow key={log.id} className={`hover:bg-muted/30 ${!log.success ? 'bg-red-50/50 dark:bg-red-950/20' : ''}`}>
                                            <TableCell className="pl-4">
                                                <span className="text-sm font-mono">
                                                    {formatLocalDateTime(log.timestamp)}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {getEventTypeBadge(log.eventType, log.success)}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-xs text-muted-foreground font-mono">{log.principalName}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    <p className="text-sm font-medium">{log.clientName}</p>
                                                    {log.appName && (
                                                        <p className="text-xs text-muted-foreground font-mono">{log.appName}</p>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {log.secretLabel ? (
                                                    <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                                                        {log.secretLabel}
                                                    </code>
                                                ) : (
                                                    <span className="text-xs text-muted-foreground">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    <span className="font-mono text-sm">{log.sourceIp}</span>
                                                    {
                                                    /**
                                                        log.userAgent && (
                                                        <p className="text-xs text-muted-foreground font-mono truncate">{getUserAgentDisplay(log.userAgent)}</p>
                                                    ) */}
                                                </div>
                                            </TableCell>

                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {renderPagination(auditPage, filteredAuditLogs.length, setAuditPage)}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <Dialog open={isCreateClientOpen} onOpenChange={setIsCreateClientOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-blue-600" />
                            Create Client Group
                        </DialogTitle>
                        <DialogDescription>
                            Create a client group to organize apps. Apps under this group will have their own credentials.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="client-name">Client Name</Label>
                            <Input
                                id="client-name"
                                placeholder="e.g., Payment Gateway"
                                value={newClientName}
                                onChange={(e) => setNewClientName(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="client-desc">Description</Label>
                            <Textarea
                                id="client-desc"
                                placeholder="Brief description of this client's purpose..."
                                value={newClientDescription}
                                onChange={(e) => setNewClientDescription(e.target.value)}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsCreateClientOpen(false)}>Cancel</Button>
                        <Button onClick={handleCreateClient} className="bg-blue-600 hover:bg-blue-700" disabled={isSavingClient || !newClientName.trim()}>
                            Create Client
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isCreateAppOpen} onOpenChange={setIsCreateAppOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AppWindow className="w-5 h-5 text-blue-600" />
                            Create New App
                        </DialogTitle>
                        <DialogDescription>
                            Register an application that will authenticate and access secrets.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="app-client">Client</Label>
                            <Select value={newAppClientId} onValueChange={setNewAppClientId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a client" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clients.filter(c => c.active).map(c => (
                                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="app-name">App Name</Label>
                            <Input
                                id="app-name"
                                placeholder="e.g., payment-api-prod"
                                value={newAppName}
                                onChange={(e) => setNewAppName(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Scopes</Label>
                            <div className="flex gap-4">
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="scope-read"
                                        checked={newAppScopes.includes("read")}
                                        onCheckedChange={(checked) => {
                                            if (checked) setNewAppScopes([...newAppScopes, "read"]);
                                            else setNewAppScopes(newAppScopes.filter(s => s !== "read"));
                                        }}
                                    />
                                    <Label htmlFor="scope-read" className="text-sm">Read</Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="scope-write"
                                        checked={newAppScopes.includes("write")}
                                        onCheckedChange={(checked) => {
                                            if (checked) setNewAppScopes([...newAppScopes, "write"]);
                                            else setNewAppScopes(newAppScopes.filter(s => s !== "write"));
                                        }}
                                    />
                                    <Label htmlFor="scope-write" className="text-sm">Write</Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="scope-delete"
                                        checked={newAppScopes.includes("delete")}
                                        onCheckedChange={(checked) => {
                                            if (checked) setNewAppScopes([...newAppScopes, "delete"]);
                                            else setNewAppScopes(newAppScopes.filter(s => s !== "delete"));
                                        }}
                                    />
                                    <Label htmlFor="scope-delete" className="text-sm text-red-600">Delete</Label>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="app-labels">Allowed Labels</Label>
                            <Input
                                id="app-labels"
                                placeholder="db, api, internal (comma-separated)"
                                value={newAppLabels}
                                onChange={(e) => setNewAppLabels(e.target.value)}
                            />
                            <p className="text-xs text-muted-foreground">
                                Labels this app can access. Separate multiple labels with commas.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsCreateAppOpen(false)}>Cancel</Button>
                        <Button onClick={handleCreateApp} className="bg-blue-600 hover:bg-blue-700" disabled={isSavingApp || !newAppClientId.trim() || !newAppName.trim() || newAppScopes.length === 0}>
                            Create App
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isCreateSecretOpen} onOpenChange={setIsCreateSecretOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Lock className="w-5 h-5 text-amber-600" />
                            Create New Secret
                        </DialogTitle>
                        <DialogDescription>
                            Store a new secret in the vault. The secret will be encrypted at rest.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="secret-client">Client</Label>
                            <Select value={newSecretClientId} onValueChange={setNewSecretClientId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a client" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clients.filter(c => c.active).map(c => (
                                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="secret-label">Secret Label</Label>
                            <Input
                                id="secret-label"
                                placeholder="e.g., prod/payments/db_master"
                                value={newSecretLabel}
                                onChange={(e) => setNewSecretLabel(e.target.value)}
                            />
                            <p className="text-xs text-muted-foreground">
                                Use path-style labels for organization (e.g., env/service/name)
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="secret-value">Secret Value</Label>
                            <Textarea
                                id="secret-value"
                                placeholder="Enter the secret value..."
                                value={newSecretValue}
                                onChange={(e) => setNewSecretValue(e.target.value)}
                                className="font-mono"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsCreateSecretOpen(false)}>Cancel</Button>
                        <Button onClick={handleCreateSecret} className="bg-blue-600 hover:bg-blue-700" disabled={isSavingSecret || !newSecretClientId.trim() || !newSecretLabel.trim() || !newSecretValue.trim()}>
                            Store Secret
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isCredentialsDialogOpen} onOpenChange={setIsCredentialsDialogOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                            App Created Successfully
                        </DialogTitle>
                        <DialogDescription>
                            Save these credentials now. The secret will not be shown again.
                        </DialogDescription>
                    </DialogHeader>
                    {generatedCredentials && (
                        <div className="space-y-4 py-4">
                            <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                                    <div className="text-sm text-amber-800 dark:text-amber-200">
                                        <p className="font-semibold">Important: Save these credentials</p>
                                        <p className="mt-1">Store these securely in your application configuration.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Client Name</Label>
                                    <div className="font-medium">{generatedCredentials.clientName}</div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Client ID</Label>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 text-sm bg-muted px-3 py-2 rounded font-mono break-all">
                                            {generatedCredentials.appId}
                                        </code>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => copyToClipboard(generatedCredentials.appId || "", "App ID")}
                                        >
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Client Secret</Label>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 text-sm bg-muted px-3 py-2 rounded font-mono break-all">
                                            {showGeneratedSecret ? generatedCredentials.clientSecret : "••••••••••••••••••••••••••••••••"}
                                        </code>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => setShowGeneratedSecret(!showGeneratedSecret)}
                                        >
                                            {showGeneratedSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => copyToClipboard(generatedCredentials.clientSecret, "Client Secret")}
                                        >
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                                <h4 className="font-semibold text-sm text-blue-900 dark:text-blue-100 mb-2">Authentication Flow</h4>
                                <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-decimal list-inside">
                                    <li>Use Client ID + Secret to request a short-lived access token</li>
                                    <li>Include the access token in API requests</li>
                                    <li>Access only secrets where label matches your allowed labels</li>
                                    <li>Client must refresh token before expiry</li>
                                </ol>
                                <p className="text-xs text-blue-600 dark:text-blue-300 mt-2">
                                    Example: If allowed labels = ["db", "api"], you can access secrets like "prod/db/master" or "prod/api/key"
                                </p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            onClick={() => {
                                setIsCredentialsDialogOpen(false);
                                setGeneratedCredentials(null);
                                toast({
                                    title: "Client Ready",
                                    description: "Client credentials have been generated. Configure your app now."
                                });
                            }}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            I've Saved the Credentials
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isViewCredentialsOpen} onOpenChange={setIsViewCredentialsOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Key className="w-5 h-5 text-blue-600" />
                            App Credentials
                        </DialogTitle>
                        <DialogDescription>
                            Credentials for authenticating this application with the Vault Service.
                        </DialogDescription>
                    </DialogHeader>
                    {viewingApp && (
                        <div className="space-y-4 py-4">
                            <div className="space-y-3">
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">App Name</Label>
                                    <div className="font-medium font-mono">{viewingApp.name}</div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Client Group</Label>
                                    <div className="font-medium">{viewingApp.clientName}</div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Client ID</Label>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 text-sm bg-muted px-3 py-2 rounded font-mono break-all">
                                            {viewingApp.appId}
                                        </code>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => copyToClipboard(viewingApp.clientId, "Client ID")}
                                        >
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Client Secret</Label>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 text-sm bg-muted px-3 py-2 rounded font-mono break-all">
                                            {showViewingSecret ? viewingApp.clientSecret : "••••••••••••••••••••••••••••••••"}
                                        </code>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => setShowViewingSecret(!showViewingSecret)}
                                        >
                                            {showViewingSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => copyToClipboard(viewingApp.clientSecret, "Client Secret")}
                                        >
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Scopes</Label>
                                    <div className="flex gap-1">
                                        {viewingApp.scopes.map(scope => (
                                            <Badge
                                                key={scope}
                                                variant="outline"
                                                className={`text-xs ${scope === "delete" ? "border-red-200 text-red-700 dark:border-red-800 dark:text-red-300" :
                                                    scope === "write" ? "border-amber-200 text-amber-700 dark:border-amber-800 dark:text-amber-300" :
                                                        "border-blue-200 text-blue-700 dark:border-blue-800 dark:text-blue-300"
                                                    }`}
                                            >
                                                {scope}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Allowed Labels</Label>
                                    <div className="flex flex-wrap gap-1">
                                        {viewingApp.allowedLabels.map(label => (
                                            <Badge key={label} variant="secondary" className="text-xs">{label}</Badge>
                                        ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        This app can access secrets where the label contains any of these values.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsViewCredentialsOpen(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isEditClientOpen} onOpenChange={setIsEditClientOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit className="w-5 h-5 text-blue-600" />
                            Edit Client Group
                        </DialogTitle>
                        <DialogDescription>
                            Update the client group name and description.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="editClientName">Client Name</Label>
                            <Input
                                id="editClientName"
                                value={editClientName}
                                onChange={(e) => setEditClientName(e.target.value)}
                                placeholder="e.g., Payment Gateway"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="editClientDescription">Description</Label>
                            <Textarea
                                id="editClientDescription"
                                value={editClientDescription}
                                onChange={(e) => setEditClientDescription(e.target.value)}
                                placeholder="Describe the purpose of this client group..."
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setIsEditClientOpen(false)}>Cancel</Button>
                        <Button onClick={handleEditClient} className="bg-blue-600 hover:bg-blue-700">Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isDeleteClientOpen} onOpenChange={setIsDeleteClientOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Delete Client Group
                        </DialogTitle>
                        <DialogDescription>
                            This action cannot be undone. This will permanently delete the client group, all its apps, and revoke all associated tokens.
                        </DialogDescription>
                    </DialogHeader>
                    {deletingClient && (
                        <div className="py-4">
                            <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg space-y-2">
                                <p className="font-medium">{deletingClient.name}</p>
                                <p className="text-sm text-muted-foreground">{deletingClient.description}</p>
                                <div className="flex gap-4 text-sm">
                                    <span><strong>{deletingClient.appsCount}</strong> app(s)</span>
                                    <span><strong>{deletingClient.secretsCount}</strong> secret(s)</span>
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setIsDeleteClientOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDeleteClient}>Delete Client</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isEditAppOpen} onOpenChange={setIsEditAppOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit className="w-5 h-5 text-blue-600" />
                            Edit App
                        </DialogTitle>
                        <DialogDescription>
                            Update the app configuration and access policies.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="editAppName">App Name</Label>
                            <Input
                                id="editAppName"
                                value={editAppName}
                                onChange={(e) => setEditAppName(e.target.value)}
                                placeholder="e.g., payment-api-prod"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Scopes</Label>
                            <div className="flex gap-4">
                                {["read", "write", "delete"].map((scope) => (
                                    <label key={scope} className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={editAppScopes.includes(scope)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setEditAppScopes([...editAppScopes, scope]);
                                                } else {
                                                    setEditAppScopes(editAppScopes.filter(s => s !== scope));
                                                }
                                            }}
                                            className="rounded border-gray-300"
                                        />
                                        <span className="text-sm capitalize">{scope}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="editAppLabels">Allowed Labels (comma-separated)</Label>
                            <Input
                                id="editAppLabels"
                                value={editAppLabels}
                                onChange={(e) => setEditAppLabels(e.target.value)}
                                placeholder="e.g., prod/payments, prod/stripe"
                            />
                            <p className="text-xs text-muted-foreground">
                                App can access secrets matching any of these label patterns.
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setIsEditAppOpen(false)}>Cancel</Button>
                        <Button onClick={handleEditApp} className="bg-blue-600 hover:bg-blue-700">Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete App Confirmation Dialog */}
            <Dialog open={isDeleteAppOpen} onOpenChange={setIsDeleteAppOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Delete App
                        </DialogTitle>
                        <DialogDescription>
                            This action cannot be undone. This will permanently delete the app and revoke all its tokens.
                        </DialogDescription>
                    </DialogHeader>
                    {deletingApp && (
                        <div className="py-4">
                            <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg space-y-2">
                                <p className="font-medium font-mono">{deletingApp.name}</p>
                                <p className="text-sm text-muted-foreground">Client: {deletingApp.clientName}</p>
                                <p className="text-sm"><strong>{deletingApp.activeTokens}</strong> active token(s) will be revoked</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setIsDeleteAppOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDeleteApp}>Delete App</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isRegenerateSecretOpen} onOpenChange={setIsRegenerateSecretOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <RefreshCw className="w-5 h-5 text-amber-600" />
                            Regenerate Client Secret
                        </DialogTitle>
                        <DialogDescription>
                            Generate a new client secret for this app. The old secret will be invalidated immediately.
                        </DialogDescription>
                    </DialogHeader>
                    {regeneratingApp && (
                        <div className="space-y-4 py-4">
                            {!newlyGeneratedSecret ? (
                                <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                                        <div className="text-sm text-amber-800 dark:text-amber-200">
                                            <p className="font-semibold">Warning: This action is irreversible</p>
                                            <p className="mt-1">
                                                The current secret for <strong>{regeneratingApp.name}</strong> will be invalidated.
                                                All active tokens will continue to work until they expire.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                                        <p className="text-sm text-green-800 dark:text-green-200 font-medium flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4" /> New secret generated successfully
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">New Client Secret</Label>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 text-sm bg-muted px-3 py-2 rounded font-mono break-all">
                                                {showNewlyGeneratedSecret ? newlyGeneratedSecret : "••••••••••••••••••••••••••••••••"}
                                            </code>
                                            <Button variant="outline" size="icon" onClick={() => setShowNewlyGeneratedSecret(!showNewlyGeneratedSecret)}>
                                                {showNewlyGeneratedSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </Button>
                                            <Button variant="outline" size="icon" onClick={() => copyToClipboard(newlyGeneratedSecret, "Client Secret")}>
                                                <Copy className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        {!newlyGeneratedSecret ? (
                            <>
                                <Button variant="outline" onClick={() => setIsRegenerateSecretOpen(false)}>Cancel</Button>
                                <Button onClick={handleConfirmRegenerateSecret} className="bg-amber-600 hover:bg-amber-700">Regenerate Secret</Button>
                            </>
                        ) : (
                            <Button onClick={() => setIsRegenerateSecretOpen(false)} className="bg-blue-600 hover:bg-blue-700">Done</Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isViewSecretOpen} onOpenChange={setIsViewSecretOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Lock className="w-5 h-5 text-amber-600" />
                            View Secret
                        </DialogTitle>
                        <DialogDescription>
                            Secret value stored in the vault.
                        </DialogDescription>
                    </DialogHeader>
                    {viewingSecret && (
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Label</Label>
                                <p className="font-mono text-sm">{viewingSecret.label}</p>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Vault Path</Label>
                                <code className="text-xs bg-muted px-2 py-1 rounded font-mono block">{viewingSecret.vaultPath}</code>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Secret Value (v{viewingSecret.version})</Label>
                                    <Button variant="ghost" size="sm" onClick={() => setShowSecretValue(!showSecretValue)}>
                                        {showSecretValue ? <><EyeOff className="w-3 h-3 mr-1" /> Hide</> : <><Eye className="w-3 h-3 mr-1" /> Reveal</>}
                                    </Button>
                                </div>
                                <div className="relative">
                                    <pre className={`text-sm bg-muted p-3 rounded font-mono overflow-x-auto ${!showSecretValue ? 'blur-sm select-none' : ''}`}>
                                        {secretValue}
                                    </pre>
                                    {showSecretValue && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="absolute top-2 right-2"
                                            onClick={() => copyToClipboard(secretValue, "Secret Value")}
                                        >
                                            <Copy className="w-3 h-3 mr-1" /> Copy
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsViewSecretOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isUpdateSecretOpen} onOpenChange={setIsUpdateSecretOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit className="w-5 h-5 text-blue-600" />
                            Update Secret
                        </DialogTitle>
                        <DialogDescription>
                            Create a new version of this secret. The previous version will be retained in history.
                        </DialogDescription>
                    </DialogHeader>
                    {updatingSecret && (
                        <div className="space-y-4 py-4">
                            <div className="p-3 bg-muted rounded-lg">
                                <p className="text-sm font-mono">{updatingSecret.label}</p>
                                <p className="text-xs text-muted-foreground mt-1">Current version: v{updatingSecret.version}</p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="updateSecretValue">New Secret Value (JSON)</Label>
                                <Textarea
                                    id="updateSecretValue"
                                    value={updateSecretValue}
                                    onChange={(e) => setUpdateSecretValue(e.target.value)}
                                    placeholder='{"username": "new_user", "password": "new_password"}'
                                    rows={6}
                                    className="font-mono text-sm"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setIsUpdateSecretOpen(false)}>Cancel</Button>
                        <Button onClick={handleUpdateSecret} className="bg-blue-600 hover:bg-blue-700">Update Secret</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isVersionHistoryOpen} onOpenChange={setIsVersionHistoryOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <History className="w-5 h-5 text-blue-600" />
                            Version History
                        </DialogTitle>
                        <DialogDescription>
                            View all versions of this secret.
                        </DialogDescription>
                    </DialogHeader>
                    {versionHistorySecret && (
                        <div className="space-y-4 py-4">
                            <div className="p-3 bg-muted rounded-lg">
                                <p className="text-sm font-mono">{versionHistorySecret.label}</p>
                            </div>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {Array.from({ length: versionHistorySecret.version }, (_, i) => versionHistorySecret.version - i).map((v) => (
                                    <div key={v} className={`p-3 rounded-lg border ${v === versionHistorySecret.version ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30' : 'border-border'}`}>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Badge variant={v === versionHistorySecret.version ? "default" : "outline"} className="font-mono">
                                                    v{v}
                                                </Badge>
                                                {v === versionHistorySecret.version && (
                                                    <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300">Current</Badge>
                                                )}
                                            </div>
                                            <span className="text-xs text-muted-foreground">
                                                {new Date(new Date(versionHistorySecret.updatedAt).getTime() - (versionHistorySecret.version - v) * 86400000 * 7).toLocaleDateString()}
                                            </span>
                                        </div>
                                        {v !== versionHistorySecret.version && (
                                            <div className="mt-2 flex gap-2">
                                                <Button variant="outline" size="sm" className="h-7 text-xs">View</Button>
                                                <Button variant="outline" size="sm" className="h-7 text-xs">Rollback</Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsVersionHistoryOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isDeleteSecretOpen} onOpenChange={setIsDeleteSecretOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Delete Secret
                        </DialogTitle>
                        <DialogDescription>
                            This action cannot be undone. This will permanently delete the secret and all its versions from the vault.
                        </DialogDescription>
                    </DialogHeader>
                    {deletingSecret && (
                        <div className="py-4">
                            <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg space-y-2">
                                <p className="font-medium font-mono">{deletingSecret.label}</p>
                                <p className="text-sm text-muted-foreground">Client: {deletingSecret.clientName}</p>
                                <p className="text-sm"><strong>{deletingSecret.version}</strong> version(s) will be deleted</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setIsDeleteSecretOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDeleteSecret}>Delete Secret</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isRevokeTokenOpen} onOpenChange={setIsRevokeTokenOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <Ban className="w-5 h-5" />
                            Revoke Token
                        </DialogTitle>
                        <DialogDescription>
                            This action cannot be undone. The token will be immediately invalidated.
                        </DialogDescription>
                    </DialogHeader>
                    {revokingToken && (
                        <div className="py-4">
                            <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg space-y-2">
                                <p className="font-medium">{revokingToken.appName}</p>
                                <p className="text-sm text-muted-foreground">Client: {revokingToken.clientName}</p>

                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setIsRevokeTokenOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleConfirmRevokeToken}>Revoke Token</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isDeactivateClientOpen} onOpenChange={setIsDeactivateClientOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-600">
                            <XCircle className="w-5 h-5" />
                            {deactivatingClient?.active ? "Deactivate Client Group" : "Activate Client Group"}
                        </DialogTitle>
                        <DialogDescription>
                            {deactivatingClient?.active
                                ? "Deactivating this client group will have the following effects:"
                                : "Activating this client group will restore access for all its apps."}
                        </DialogDescription>
                    </DialogHeader>
                    {deactivatingClient && deactivatingClient.active && (
                        <div className="py-4">
                            <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg space-y-3">
                                <p className="font-medium">{deactivatingClient.name}</p>
                                <ul className="text-sm space-y-2 text-amber-800 dark:text-amber-200">
                                    <li className="flex items-center gap-2">
                                        <XCircle className="w-4 h-4" />
                                        All {clientApps.filter(a => a.clientId === deactivatingClient.id).length} app(s) will be deactivated
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Ban className="w-4 h-4" />
                                        All active tokens will be immediately revoked
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Lock className="w-4 h-4" />
                                        Apps will not be able to access secrets
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setIsDeactivateClientOpen(false)}>Cancel</Button>
                        <Button
                            onClick={handleConfirmDeactivateClient}
                            className={deactivatingClient?.active ? "bg-amber-600 hover:bg-amber-700" : "bg-green-600 hover:bg-green-700"}
                        >
                            {deactivatingClient?.active ? "Deactivate" : "Activate"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isDeactivateAppOpen} onOpenChange={setIsDeactivateAppOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-600">
                            <XCircle className="w-5 h-5" />
                            {deactivatingApp?.active ? "Deactivate App" : "Activate App"}
                        </DialogTitle>
                        <DialogDescription>
                            {deactivatingApp?.active
                                ? "Deactivating this app will have the following effects:"
                                : "Activating this app will restore its access to secrets."}
                        </DialogDescription>
                    </DialogHeader>
                    {deactivatingApp && deactivatingApp.active && (
                        <div className="py-4">
                            <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg space-y-3">
                                <p className="font-medium font-mono">{deactivatingApp.name}</p>
                                <p className="text-sm text-muted-foreground">Client: {deactivatingApp.clientName}</p>
                                <ul className="text-sm space-y-2 text-amber-800 dark:text-amber-200">
                                    <li className="flex items-center gap-2">
                                        <Ban className="w-4 h-4" />
                                        {deactivatingApp.activeTokens} active token(s) will be revoked
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Lock className="w-4 h-4" />
                                        App will not be able to authenticate or access secrets
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setIsDeactivateAppOpen(false)}>Cancel</Button>
                        <Button
                            onClick={handleConfirmDeactivateApp}
                            className={deactivatingApp?.active ? "bg-amber-600 hover:bg-amber-700" : "bg-green-600 hover:bg-green-700"}
                        >
                            {deactivatingApp?.active ? "Deactivate" : "Activate"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isRevokeAllTokensOpen} onOpenChange={setIsRevokeAllTokensOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <Ban className="w-5 h-5" />
                            Revoke All Tokens
                        </DialogTitle>
                        <DialogDescription>
                            This will immediately invalidate all active tokens for this app.
                        </DialogDescription>
                    </DialogHeader>
                    {revokeAllTokensApp && (
                        <div className="py-4">
                            <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg space-y-2">
                                <p className="font-medium font-mono">{revokeAllTokensApp.name}</p>
                                <p className="text-sm text-muted-foreground">Client: {revokeAllTokensApp.clientName}</p>
                                <p className="text-sm">
                                    <strong>{revokeAllTokensApp.activeTokens}</strong> active token(s) will be revoked
                                </p>
                                <p className="text-xs text-muted-foreground mt-2">
                                    The app will need to re-authenticate to obtain new tokens.
                                </p>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setIsRevokeAllTokensOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleConfirmRevokeAllTokens}>Revoke All Tokens</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
